import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroData, designStyles, approachSteps, testimonials, siteConfig, catapultFeaturedHome } from "../data/mock";
import { ArrowRight, ArrowDown, Quote, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.15, ...options }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
};

const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={heroData.backgroundImage}
          alt="Luxury home interior"
          className="w-full h-full object-cover"
          style={{ filter: "brightness(0.4)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
      </div>

      <div className="relative z-10 h-full flex flex-col justify-end pb-24 md:pb-32 px-6 md:px-12 max-w-[1400px] mx-auto">
        <div
          className={`transition-all duration-1000 delay-300 ${
            loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[1.05] mb-6 whitespace-pre-line">
            {heroData.headline}
          </h1>
          <p
            className={`font-body text-white/70 text-base md:text-lg max-w-xl leading-relaxed mb-10 transition-all duration-1000 delay-500 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {heroData.subheadline}
          </p>
          <div
            className={`flex flex-col sm:flex-row items-start gap-4 transition-all duration-1000 delay-700 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <Link
              to="/catapult/journey"
              className="inline-flex items-center gap-3 font-body text-sm tracking-[0.1em] uppercase px-8 py-4 bg-white text-stone-900 hover:bg-stone-100 transition-colors duration-300 group"
            >
              {heroData.ctaPrimary}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/catapult/styles"
              className="inline-flex items-center gap-3 font-body text-sm tracking-[0.1em] uppercase px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors duration-300"
            >
              {heroData.ctaSecondary}
            </Link>
          </div>
        </div>

        <div
          className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-1000 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        >
          <ArrowDown size={20} className="text-white/40 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

const PhilosophySection = () => {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-28 md:py-40 px-6 md:px-12 bg-[#FAFAF7]">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div
            className={`transition-all duration-1000 ${
              inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
          >
            <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
              Our Philosophy
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] text-stone-900 leading-[1.1] mb-8">
              We design homes,<br />not just build them.
            </h2>
            <p className="font-body text-stone-500 text-base leading-relaxed mb-8 max-w-lg">
              Every home begins as a conversation. We listen to how you live, what
              inspires you, and what makes a space feel like yours. Then we design
              around that — creating homes that are as unique as the families who
              live in them.
            </p>
            <Link
              to="/catapult/approach"
              className="inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase text-stone-900 group"
            >
              Discover Our Approach
              <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
          </div>
          <div
            className={`relative transition-all duration-1000 delay-300 ${
              inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/6585741/pexels-photo-6585741.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Luxury bathroom design"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 md:w-40 md:h-40 bg-[#B8977E]/10 -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

const StylesPreview = () => {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-28 md:py-40 px-6 md:px-12 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div
          className={`mb-16 md:mb-20 transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
            Design Styles
          </span>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2 className="font-display text-4xl md:text-5xl text-stone-900 leading-[1.1]">
              Find your aesthetic
            </h2>
            <Link
              to="/catapult/styles"
              className="inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase text-stone-500 hover:text-stone-900 transition-colors duration-300 group"
            >
              View all styles
              <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {designStyles.map((style, i) => (
            <Link
              to="/catapult/styles"
              key={style.id}
              className={`group relative aspect-[3/2] overflow-hidden cursor-pointer transition-all duration-1000 ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <img
                src={style.image}
                alt={style.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <span className="font-body text-[11px] tracking-[0.2em] uppercase text-white/60 mb-2 block">
                  {style.subtitle}
                </span>
                <h3 className="font-display text-2xl md:text-3xl text-white">
                  {style.title}
                </h3>
              </div>
              <div className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center border border-white/30 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0 translate-x-2">
                <ArrowRight size={16} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProcessPreview = () => {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-28 md:py-40 px-6 md:px-12 bg-stone-950">
      <div className="max-w-[1400px] mx-auto">
        <div
          className={`mb-16 md:mb-20 transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
            Our Process
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.1] max-w-2xl">
            From vision to reality,<br />guided every step
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {approachSteps.map((step, i) => (
            <div
              key={step.number}
              className={`transition-all duration-1000 ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <span className="font-display text-5xl md:text-6xl text-white/10 block mb-4">
                {step.number}
              </span>
              <h3 className="font-display text-xl text-white mb-3">
                {step.title}
              </h3>
              <p className="font-body text-sm text-white/40 leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        <div
          className={`mt-16 transition-all duration-1000 delay-600 ${
            inView ? "opacity-100" : "opacity-0"
          }`}
        >
          <Link
            to="/catapult/approach"
            className="inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase text-white/60 hover:text-white transition-colors duration-300 group"
          >
            Learn more about our approach
            <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const [ref, inView] = useInView();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section ref={ref} className="py-28 md:py-40 px-6 md:px-12 bg-[#FAFAF7]">
      <div className="max-w-[1000px] mx-auto text-center">
        <span
          className={`font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-12 block transition-all duration-1000 ${
            inView ? "opacity-100" : "opacity-0"
          }`}
        >
          Client Stories
        </span>

        <div className="relative min-h-[280px] md:min-h-[220px]">
          {testimonials.map((testimonial, i) => (
            <div
              key={testimonial.id}
              className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-700 ${
                i === activeIndex
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4 pointer-events-none"
              }`}
            >
              <Quote size={32} className="text-[#B8977E]/30 mb-6" strokeWidth={1} />
              <p className="font-display text-xl md:text-2xl lg:text-[28px] text-stone-800 leading-relaxed mb-8 italic">
                "{testimonial.quote}"
              </p>
              <div>
                <p className="font-body text-sm text-stone-900 font-medium">
                  {testimonial.author}
                </p>
                <p className="font-body text-xs text-stone-400 mt-1">
                  {testimonial.location} &middot; {testimonial.project}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-3 mt-10">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`h-[2px] transition-all duration-500 ${
                i === activeIndex ? "w-10 bg-stone-900" : "w-6 bg-stone-300"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const InvestmentCTA = () => {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-28 md:py-40 px-6 md:px-12 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div
          className={`flex flex-col lg:flex-row items-center gap-16 lg:gap-24 transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <div className="flex-1">
            <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
              Investment
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-stone-900 leading-[1.1] mb-6">
              Custom homes starting<br />at {siteConfig.minBudget}
            </h2>
            <p className="font-body text-stone-500 text-base leading-relaxed mb-8 max-w-lg">
              Building a custom home is one of life's most meaningful investments.
              We believe in transparency and ensuring our partnership is the right
              fit from the very beginning.
            </p>
            <Link
              to="/catapult/investment"
              className="inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase text-stone-900 group"
            >
              Learn About Investment
              <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
          </div>
          <div className="flex-1 w-full">
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1640109478916-f445f8f19b11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzcxNTUyMDc0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Modern interior"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="aspect-[3/4] overflow-hidden mt-8">
                <img
                  src="https://images.pexels.com/photos/6920614/pexels-photo-6920614.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Luxury bathroom"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function HomePage() {
  return (
    <main>
      <HeroSection />
      <FeaturedHomeSection />
      <PhilosophySection />
      <StylesPreview />
      <ProcessPreview />
      <TestimonialsSection />
      <InvestmentCTA />
    </main>
  );
}

const FeaturedHomeSection = () => {
  const [ref, inView] = useInView();
  const fh = catapultFeaturedHome;
  const photos = fh.photos || [];
  const [idx, setIdx] = useState(0);

  const goPrev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);
  const goNext = () => setIdx((i) => (i + 1) % photos.length);

  // Keyboard arrows when the section is in view
  useEffect(() => {
    if (!inView) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, photos.length]);

  const active = photos[idx];

  return (
    <section
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 bg-stone-950 text-white overflow-hidden"
      data-testid="catapult-featured-home"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* LEFT — copy + stats */}
        <div
          className={`lg:col-span-5 transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <span
              className="inline-flex items-center gap-2 font-body text-[10px] tracking-[0.25em] uppercase text-stone-950 bg-[#B8977E] px-3 py-1.5 font-semibold"
              data-testid="featured-home-badge"
            >
              <span className="w-1.5 h-1.5 bg-stone-950 rounded-full animate-pulse" />
              New Listing
            </span>
            <span className="font-body text-[11px] tracking-[0.3em] uppercase text-white/50">
              Featured Home · Built 2026
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] mb-4">
            {fh.address}
          </h2>
          <p className="font-body text-white/40 text-sm tracking-[0.12em] uppercase mb-8">
            {fh.city}
          </p>

          <p className="font-display text-xl md:text-2xl text-white/85 leading-snug mb-6">
            {fh.headline}
          </p>
          <p className="font-body text-white/55 text-base leading-relaxed mb-10 max-w-lg">
            {fh.blurb}
          </p>

          {/* Stats row */}
          <div className="mb-10 pb-8 border-b border-white/10 max-w-md">
            <div className="mb-6">
              <div className="font-display text-3xl md:text-4xl text-white leading-none">{fh.price}</div>
              <div className="font-body text-[10px] tracking-[0.15em] uppercase text-white/40 mt-2">Listed Price</div>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="font-display text-2xl md:text-3xl text-white">{fh.beds}</div>
                <div className="font-body text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1">Beds</div>
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl text-white">{fh.baths}</div>
                <div className="font-body text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1">Baths</div>
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl text-white">{fh.sqft}</div>
                <div className="font-body text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1">Sq Ft</div>
              </div>
            </div>
          </div>

          <a
            href={fh.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 font-body text-sm tracking-[0.12em] uppercase px-8 py-4 bg-white text-stone-900 hover:bg-[#B8977E] hover:text-white transition-colors duration-300 group"
            data-testid="catapult-featured-home-link"
          >
            {fh.externalLabel || "View the Listing"}
            <ExternalLink size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* RIGHT — photo carousel */}
        <div
          className={`lg:col-span-7 transition-all duration-1000 delay-300 ${
            inView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
          }`}
          data-testid="featured-home-carousel"
        >
          <div className="relative aspect-[4/3] overflow-hidden border border-white/10 group bg-stone-900">
            {photos.map((photo, i) => (
              <img
                key={photo.src}
                src={photo.src}
                alt={photo.caption}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out ${
                  i === idx ? "opacity-100" : "opacity-0"
                }`}
                loading={i === 0 ? "eager" : "lazy"}
              />
            ))}

            {/* Gradient overlay so caption stays legible */}
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

            {/* Prev / Next */}
            <button
              onClick={goPrev}
              aria-label="Previous photo"
              data-testid="featured-home-prev"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 backdrop-blur-md text-white opacity-0 group-hover:opacity-100 hover:bg-white hover:text-stone-900 transition-all duration-300"
            >
              <ChevronLeft size={20} strokeWidth={1.5} />
            </button>
            <button
              onClick={goNext}
              aria-label="Next photo"
              data-testid="featured-home-next"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 backdrop-blur-md text-white opacity-0 group-hover:opacity-100 hover:bg-white hover:text-stone-900 transition-all duration-300"
            >
              <ChevronRight size={20} strokeWidth={1.5} />
            </button>

            {/* Counter */}
            <div className="absolute top-5 right-5 font-body text-[11px] tracking-[0.2em] uppercase text-white/80 bg-black/40 backdrop-blur-md px-3 py-1.5">
              {String(idx + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}
            </div>

            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#B8977E] mb-2 block">
                {active?.caption?.split("·")[0]?.trim()}
              </span>
              <p className="font-body text-white/90 text-sm md:text-base leading-snug max-w-md">
                {active?.caption?.split("·").slice(1).join("·").trim()}
              </p>
            </div>
          </div>

          {/* Dot indicators */}
          <div className="flex items-center gap-3 mt-6 justify-center lg:justify-start" role="tablist">
            {photos.map((p, i) => (
              <button
                key={p.src}
                onClick={() => setIdx(i)}
                aria-label={`Photo ${i + 1}`}
                data-testid={`featured-home-dot-${i}`}
                className={`h-[2px] transition-all duration-300 ${
                  i === idx ? "w-10 bg-[#B8977E]" : "w-5 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
